<template>
  <div class="dropdown-wrapper">
    <div
      v-if="label"
      class="input-label-container"
    >
      <label
        class="input-label"
      >{{ label }}</label>
      <span
        v-if="showRequired"
        class="required-label"
      >(required)</span>
      <span
        v-show="modelValue !== undefined && modelValue !== ''"
        class="clear-button"
        @click="onClearClicked"
      > Clear </span>
    </div>
    <select
      :required="(modelValue === undefined || modelValue === '')? true : false"
      :disabled="disabled ? true : false"
      :value="modelValue"
      class="dropdown-button"
      @change="onChanged"
    >
      <option
        value=""
        disabled
        selected
      >
        Choose Option
      </option>
      <option
        v-for="option in activeOptions"
        :key="option.id"
        :value="option.id"
        class="dropdown-option"
      >
        {{ option.name }}
      </option>
    </select>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'

export default defineComponent({
  name: 'Dropdown',
  props: {
    modelValue: {
      type: [String, Number],
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    options: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      type: Array as PropType<Array<DropdownOptionResponse<any>>>,
      required: true
    },
    showInactive: {
      type: Boolean,
      default: false
    },
    showRequired: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'update:modelValue'
  ],
  computed: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    activeOptions(): DropdownOptionResponse<any>[] {
      return this.options.filter(o => o.active || this.showInactive)
    }
  },
  methods: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChanged(e: any): void {
      const value = e.currentTarget.value
      const index = this.options.findIndex(o => o.id === value || o.id === parseInt(value))
      if (index === -1)
        this.onClearClicked()
      else
        this.$emit('update:modelValue', this.options[index].id)
    },
    onClearClicked(): void {
      if (typeof(this.modelValue) === 'string')
        this.$emit('update:modelValue', '')
      else
        this.$emit('update:modelValue', undefined)
    }
  }
})
</script>

<style scoped lang="scss">
.dropdown-wrapper {
  position: relative;

  .input-label-container {

    .input-label {
      @include input-text-field-label;
    }

    .required-label {
      @include input-text-field-label;
      margin-left: 5px;
      font-weight: 300;
    }

    .clear-button {
      position: absolute;
      top: 2px;
      right: 0;
      font-size: 13px;
      font-weight: bold;
      color: $Color-sac-orange;
      cursor: pointer;
    }
  }

  select {
    -moz-appearance: none; 
    -webkit-appearance: none; 
    appearance: none;
    background: url(data:image/svg+xml;base64,PHN2ZyBpZD0iTGF5ZXJfMSIgZGF0YS1uYW1lPSJMYXllciAxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0Ljk1IDEwIj48ZGVmcz48c3R5bGU+LmNscy0ye2ZpbGw6IzQ0NDt9PC9zdHlsZT48L2RlZnM+PHRpdGxlPmFycm93czwvdGl0bGU+PHBvbHlnb24gY2xhc3M9ImNscy0yIiBwb2ludHM9IjEuNDEgNC42NyAyLjQ4IDMuMTggMy41NCA0LjY3IDEuNDEgNC42NyIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtMiIgcG9pbnRzPSIzLjU0IDUuMzMgMi40OCA2LjgyIDEuNDEgNS4zMyAzLjU0IDUuMzMiLz48L3N2Zz4=) no-repeat 100% 50%;
    @include input-text-field;

    &:disabled {
      background: none;
      background-color: transparent;
      border-color: transparent;
      pointer-events: none;
    }

    &:invalid {
      color: $Color-font-medium;
    }

    option[value=""][disabled] {
      display: none;
    }

    option {
      color: $Color-font-high;
      font-size: 18px;
    }
  }
}
</style>
